import React, {useState, useEffect, useCallback} from 'react';
import {useTheme} from "@mui/material/styles";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import Grid from "@mui/material/Grid";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import {SchemaOf, object, number, string, boolean, date,} from "yup";
import {useAuth} from "../../_auth/useAuth";
import {
    Form, ButtonBar, DaButton,
    DaHiddenField, DaTextField,
    FormTitle, FormSectionHeader,
    ProgressPanel, MessagePanel, IMessagePanelProps, NumberFormatType, DaNumberFormatField, DaDateTimePickerField,
} from "../_shared";
import {FormPaper} from "./Licensee.styles";
import {ILicensee, licenseeApi, IItemIdRequest} from "../../_api";
import {AuthRolesEnum, licenseeAEVFields} from "../../_appData";


const licenseeSchema: SchemaOf<ILicensee> = object({
    licensee_id: number().integer().optional(),
    company_name: string().label(licenseeAEVFields.companyName.label).max(100).required(),
    phone: string().label(licenseeAEVFields.phone.label).required(),
    fax: string().label(licenseeAEVFields.fax.label).optional(),
    federal_tax_id: string().label(licenseeAEVFields.federalTaxId.label).optional(),
    officer_name: string().label(licenseeAEVFields.officerName.label).required(),
    officer_title: string().label(licenseeAEVFields.officerTitle.label).optional(),
    is_deleted: boolean().label(licenseeAEVFields.isDeleted.label).required(),
    create_datetime: date().optional(),
    modify_datetime: date().optional(),
    modify_user_name: string().optional(),
});


export const LicenseeAEView: React.FC = () => {
    const theme = useTheme();
    const { user, userHasPrivilege, getAccessToken } = useAuth();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const navigationState = location.state as { mode?: 'view' } | null;
    const isViewMode = navigationState?.mode === 'view';
    const [msgPanelProps, setMsgPanelProps] = useState<IMessagePanelProps>({message: ""});
    const [progressTitle, setProgressTitle] = useState<string>("Getting Licensee Info...");
    const [isShowError, setIsShowError] = useState<boolean>(false);

    const formFieldSize = "medium";
    const formFieldVariant = "standard";
    let licenseeId: number | undefined = undefined;
    let aevMode: "add" | "upd" | "err" | undefined = undefined;


    // Validate URL param
    if(params.id) {
        if (Number.isNaN(Number(params.id))) {
            if(params.id !== "new"){
                aevMode = "err";
            } else {
                licenseeId = -1;
                aevMode = "add";
            }
        } else {
            licenseeId = parseInt(params.id, 10);
            aevMode = "upd";
        }
    }

    // defining the initial state for the form
    const initialValues = {
        licensee_id: -1,
        company_name: '',
        phone: '',
        fax:'',
        federal_tax_id: '',
        officer_name: '',
        officer_title: '',
        is_deleted: false,
        create_datetime: new Date(),
        modify_datetime: new Date(),
        modify_user_name: `${user.account_id}`,
    };

    const [showProgress, setShowProgress] = useState<boolean>(true);

    const { control, handleSubmit, reset, formState: { errors, isValid } } = useForm<ILicensee>({
        defaultValues: initialValues,
        resolver: yupResolver(licenseeSchema)
    });

    const showError = ({title, message, type="error"}: IMessagePanelProps) => {
        const msgProps: IMessagePanelProps = {
            message: message,
            title: title,
            type: type
        }
        setMsgPanelProps(msgProps);
        setIsShowError(true);
    }

    const onSubmit: SubmitHandler<ILicensee> = (data) => {
        saveLicenseeData(data);
    };

    const saveLicenseeData = useCallback(async (options): Promise<void> => {
        setProgressTitle("Saving Licensee Info...");
        setShowProgress(true);

        const auth_token = await getAccessToken();
        let apiPromise = undefined;
        if (aevMode === "add") {
            apiPromise = licenseeApi.add(options, auth_token);
        } else if (aevMode === "upd") {
            apiPromise = licenseeApi.update(options, auth_token);
        } else {
            showError({
                title: "Inconsistent Form State Error",
                message: "An error occurred while saving Licensee data. " +
                    "Navigate to the Licensee list view and retry your last action."
            });
            setShowProgress(false);
        }

        apiPromise && apiPromise.then((resp) => {
            if (resp && resp.success){
                navigate("/licensees");
            } else {
                showError({
                    title: "Unable to Save Licensee",
                    message: "An error occurred while saving Licensee data. " +
                        "Verify that you have provided all required data and " +
                        "retry your last action."
                });
            }
        })
        .catch(() => {
            showError({
                title: "Licensee API Save Error",
                message: "An error occurred while saving Licensee data. " +
                    "There may have been a network error.",
            });
        })
        .finally(() => {
            setShowProgress(false);
        });
    }, []);

    const getLicenseeData = useCallback(async (): Promise<void> => {
        setProgressTitle("Getting Licensee Data...");
        setShowProgress(true);

            if(aevMode === "err"){
                showError({
                    title: "Invalid Licensee ID",
                    message: "An error occurred while retrieving Licensee data. " +
                        "Please go to the Licensee list view and retry your latest action."
                });
                setShowProgress(false);
            } else if(aevMode === "add") {
                // New Licensee
                setShowProgress(false);
            } else if(aevMode === "upd") {
                const options: IItemIdRequest = {id: licenseeId as number};
                const auth_token = await getAccessToken();

                licenseeApi.get_by_id(options, auth_token)
                    .then((resp) => {
                        if (resp) {
                            if(resp.success) {
                                const data = resp.data as ILicensee;
                                reset({
                                    ...data,
                                    modify_user_name: data.modify_user_name ?? '',
                                });
                            } else if(resp.error && resp.error.length > 0) {
                                showError({
                                    title: "Licensee Not Found Error",
                                    message: "An error occurred while retrieving Licensee data. " +
                                        "The Licensee data was not found.",
                                });
                            }
                        }
                    })
                    .catch(() => {
                        showError({
                            title: "Licensee API Fetch Error",
                            message: "An error occurred while fetching Licensee data. " +
                                "There may have been a network error.",
                        });
                    })
                    .finally(() => {
                        setShowProgress(false);
                    });
            }

    }, [reset, licenseeId, aevMode]);

    useEffect(() => {
        // Get data from remote service
        const resp = getLicenseeData();
    }, [getLicenseeData]);


    return (
    <>
        {licenseeId &&
        <>
            <FormPaper elevation={4}>
                {/* Form Title*/}
                <FormTitle
                    title={isViewMode
                            ? "Licensee: View"
                            : (aevMode ==="add"
                                ? "Licensee: New"
                                : "Licensee: Update")}
                    titleIcon={"corporate_fare_rounded_icon"}
                />

                <Grid container>
                    {showProgress ? (
                        <Grid item xs={12}>
                            <ProgressPanel title={progressTitle}/>
                        </Grid>
                    ) : (isShowError ? (
                        <Grid item xs={12} justifyContent={"start"}>
                            <MessagePanel
                                {...msgPanelProps}
                            />
                        </Grid>
                    ) : (
                        <Grid container item xs={12}>
                            <Form
                                autoComplete="off"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                             {/* Licensee Section Header */}
                            <Grid item xs={12}>
                                <FormSectionHeader
                                    title={"Licensee Info"}
                                    typeVariant={"h5"}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DaTextField
                                    control={control}
                                    name={licenseeAEVFields.companyName.name}
                                    label={licenseeAEVFields.companyName.label}
                                    required={licenseeAEVFields.companyName.required}
                                    readOnly={isViewMode}
                                    variant={formFieldVariant}
                                    size={formFieldSize}
                                    errors={errors}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DaNumberFormatField
                                    control={control}
                                        name={licenseeAEVFields.phone.name}
                                        label={licenseeAEVFields.phone.label}
                                        required={licenseeAEVFields.phone.required}
                                        readOnly={isViewMode}
                                        variant={formFieldVariant}
                                        size={formFieldSize}
                                        errors={errors}
                                        numberFormat={NumberFormatType.US_Phone}
                                    />
                            </Grid>
                            <Grid item xs={12}>
                                <DaTextField
                                    control={control}
                                    name={licenseeAEVFields.fax.name}
                                    label={licenseeAEVFields.fax.label}
                                    required={licenseeAEVFields.fax.required}
                                    readOnly={isViewMode}
                                    variant={formFieldVariant}
                                    size={formFieldSize}
                                    errors={errors}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DaTextField
                                    control={control}
                                    name={licenseeAEVFields.federalTaxId.name}
                                    label={licenseeAEVFields.federalTaxId.label}
                                    required={licenseeAEVFields.federalTaxId.required}
                                    readOnly={isViewMode}
                                    variant={formFieldVariant}
                                    size={formFieldSize}
                                    errors={errors}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DaTextField
                                    control={control}
                                    name={licenseeAEVFields.officerName.name}
                                    label={licenseeAEVFields.officerName.label}
                                    required={licenseeAEVFields.officerName.required}
                                    readOnly={isViewMode}
                                    variant={formFieldVariant}
                                    size={formFieldSize}
                                    errors={errors}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DaTextField
                                    control={control}
                                    name={licenseeAEVFields.officerTitle.name}
                                    label={licenseeAEVFields.officerTitle.label}
                                    required={licenseeAEVFields.officerTitle.required}
                                    readOnly={isViewMode}
                                    variant={formFieldVariant}
                                    size={formFieldSize}
                                    errors={errors}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DaDateTimePickerField
                                    control={control}
                                    name={licenseeAEVFields.modifyDatetime.name}
                                    label={licenseeAEVFields.modifyDatetime.label}
                                    readOnly
                                    variant={formFieldVariant}
                                    size={formFieldSize}
                                    disableOpenPicker={true}
                                    errors={errors}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DaTextField
                                    control={control}
                                    name={licenseeAEVFields.modifyUserName.name}
                                    label={licenseeAEVFields.modifyUserName.label}
                                    readOnly
                                    variant={formFieldVariant}
                                    size={formFieldSize}
                                    errors={errors}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DaHiddenField
                                    control={control}
                                    name={licenseeAEVFields.createDatetime.name}
                                    errors={errors}
                                />
                                <DaHiddenField
                                    control={control}
                                    name={licenseeAEVFields.licenseeId.name}
                                    errors={errors}
                                />
                                <DaHiddenField
                                    control={control}
                                    name={licenseeAEVFields.isDeleted.name}
                                    errors={errors}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <ButtonBar buttonAlignment="alignRight" >
                                    <DaButton buttonStyle="secondary" onClick={() => {navigate("/licensees")}}>
                                        Close
                                    </DaButton>
                                    {!isViewMode && userHasPrivilege([AuthRolesEnum.SystemAdmin, AuthRolesEnum.Licensee]) &&
                                        <DaButton buttonStyle="primary" type="submit">
                                            Save Licensee
                                        </DaButton>
                                    }
                                </ButtonBar>
                            </Grid>
                            </Form>
                        </Grid>
                        )
                    )}
                </Grid>
            </FormPaper>
        </>
        }
    </>
    );
}